import { firstValueFrom } from 'rxjs'
import { AlgoliaInstantsearchComponent } from '@app-domains/algolia/components/algolia-instantsearch.component'
import connectRange, {
    RangeBoundaries,
    RangeConnectorParams as CParams,
    RangeWidgetDescription as WDescription,
} from 'instantsearch.js/es/connectors/range/connectRange'
import { Renderer } from 'instantsearch.js/es/types'

import { Component, forwardRef, Inject, Input, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { FormControl } from '@angular/forms'

import { AnalyticsService } from '@app-domains/analytics/analytics.service'
import { Analytics } from '@app-domains/analytics/types/analytics.types'
import { delay } from '@app-lib/common.lib'
import { AbstractInstantsearchWidgetComponent } from '@app-domains/algolia/abstract-instantsearch-widget.component'

type State = WDescription['renderState']

@Component({
    selector: 'app-price-range',
    templateUrl: './price-range.component.html',
})
export class PriceRangeComponent extends AbstractInstantsearchWidgetComponent<WDescription, CParams> implements OnInit {

    public override readonly widgetName = 'RangeSlider'

    @Input()
    public attribute: string

    public control: FormControl
    private priceMax = 'price[max]'
    private priceMin = 'price[min]'

    public readonly labelFormatter = (v: number): string => Math.round(v).toString()

    constructor(
        @Inject(forwardRef(() => AlgoliaInstantsearchComponent))
        public instantSearchInstance: AlgoliaInstantsearchComponent,
        private analytics: AnalyticsService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super()
    }

    public override ngOnInit(): void {
        this.createWidget(connectRange, {
            attribute: this.attribute,
            precision: 0,
        })

        super.ngOnInit()
    }

    private async initialiseRefinementFromUrl(): Promise<void> {
        const queryParams = await firstValueFrom(this.route.queryParamMap)

        const rangeMin = queryParams.get(this.priceMin)
        const rangeMax = queryParams.get(this.priceMax)

        if (rangeMin == null && rangeMax == null) {
            this.resetRange(false)
        }

        if ((rangeMin != null && +rangeMin < this.state!.range!.min!) ||
            (rangeMax != null && +rangeMax > this.state!.range!.max!)
        ) {
            this.resetRange(true)
        }

        if (rangeMin != null && ! isNaN(+rangeMin)) {
            this.state!.start[0] = +rangeMin
            this.control.setValue([+rangeMin, this.control.value[1]])
        }

        if (rangeMax != null && ! isNaN(+rangeMax)) {
            this.state!.start[1] = +rangeMax
            this.control.setValue([this.control.value[0], +rangeMax])
        }

        delay(10).then(() => this.control.setValue(this.state?.start))
        this.state?.refine(this.state?.start)
    }

    public override updateState: Renderer<State, CParams> = async (state, isFirstRendering): Promise<void> => {
        if (isFirstRendering) {
            this.state = state
            return
        }

        if (! state.range.min && ! state.range.max) {
            return
        }

        if (! this.control) {
            this.control ??= new FormControl(this.state?.start)
        }

        this.state = state
        await this.initialiseRefinementFromUrl()

        // We have to delay the form control value update until the range-slider component
        // has detected any changes in the range as provided by the new state we just set.
        delay(10).then(() => {
            // We'll also defer instantiating the form control up until this point as to
            // prevent some race conditions with initial state updates. The underlying
            // range slider instance now only instantiates once we have some actual data
            // to work with.
            const filterType: Analytics.FilterTypes = 'slider'
            const filterName = 'price'
            const filterValueMin = String(state.start[0] === -Infinity ? state.range.min : state.start[0])
            const filterValueMax = String(state.start[1] === Infinity ? state.range.max : state.start[1])

            // only log when the range has been changed from min and/or max range
            if (filterValueMin !== String(state.range.min)
                || filterValueMax !== String(state.range.max)
            ) {
                this.analytics.logFilterOn(filterType, filterName, filterValueMin, filterValueMax)
            }
        })
    }

    public valueChange(event: RangeBoundaries): void {
        this.state!.refine(event)
        this.setUrlPart()
    }

    private resetRange(setUrl: boolean): void {
        this.state!.start[0] = this.state?.range.min
        this.state!.start[1] = this.state?.range.max

        // We have to delay the form control value update until the size-slider component
        // has detected any changes in the range.
        delay(10).then(() => this.control.setValue(this.state?.start))

        if (setUrl) {
            this.setUrlPart()
        }

        return
    }

    private setUrlPart(): void {
        const controlValue = this.control.value

        // Check if the control values are equal to the state's range values
        const isMinDefault = controlValue[0] === this.state?.range.min
        const isMaxDefault = controlValue[1] === this.state?.range.max

        const noValueChange = isMinDefault && isMaxDefault

        void this.router.navigate(
            [],
            {
                relativeTo: this.route,
                queryParams: {
                    [this.priceMin]: noValueChange ? null : controlValue[0],
                    [this.priceMax]: noValueChange ? null : controlValue[1],
                },
                queryParamsHandling: 'merge',
            },
        )
    }

}
